// 2D map (minimap + fullscreen map): lightweight canvas slippy map, see createMapView().

import type { LakeFile, LatLon, MapView } from '../contracts';
import {
  appendCulled,
  appendFinishSquares,
  bboxVisible,
  projectLake,
  type CullRect,
  type ProjectedLake,
  type ScreenXform,
} from './geometry';
import { clientToLocal, normDeg } from './input';
import {
  latToY,
  lonToX,
  metersPerPixel,
  niceScale,
  panBy,
  placeWorldAt,
  screenToWorld,
  tileRange,
  worldSize,
  worldToScreen,
  xToLon,
  yToLat,
  zoomAround,
  type Point,
  type View,
} from './mercator';
import { OSM_TRANSPORT_ICON_COLOR, drawOsmIcon, osmIcon } from '../assets/osmIcons';
import { ensureMapStyles } from './styles';
import { TileManager, tileZoomFor, type TileSource, type TileStats } from './tiles';
import { createShortbreadSource } from './vectorTiles';

export type MapMode = 'mini' | 'full' | 'hidden';

export interface MapViewOptions {
  /** Load basemap tiles (default true). Tests may disable them. */
  tiles?: boolean;
  tileSource?: TileSource;
  /** Zoom of the minimap (default 15). */
  miniZoom?: number;
  /** Initial zoom of the fullscreen map (default 15). */
  fullZoom?: number;
}

export interface MapViewState {
  lat: number;
  lon: number;
  zoom: number;
  follow: boolean;
  mode: MapMode;
  /** Local (unrotated) size in CSS px. */
  width: number;
  height: number;
  rotation: number;
}

export interface MapViewStats extends TileStats {
  draws: number;
  lastDrawMs: number;
  maxDrawMs: number;
}

/** MapView plus non-contract helpers (harness, tests; candidates for the contract). */
export interface MapViewExt extends MapView {
  getView(): MapViewState;
  /** Show a place (stops following the user until "Zentrieren"). */
  setView(center: LatLon, zoom: number): void;
  /**
   * Last-known (cached) position for display only, e.g. from localStorage at startup. Used to
   * centre the map and draw a hollow/grey "alte Position" marker only while there is no live fix
   * (setUser) yet in this session; a live fix always takes priority and is never overwritten by this.
   */
  setCachedUser(p: LatLon | null, accuracyM: number | null): void;
  /** Element-local CSS px (unrotated frame) -> WGS84. */
  localToLatLon(x: number, y: number): LatLon;
  latLonToLocal(p: LatLon): Point;
  stats(): MapViewStats;
}

// Style constants (CSS px unless noted).
const MIN_ZOOM = 5;
const MAX_ZOOM = 19;
const MINI_INTERVAL_MS = 500; // minimap redraws at most 2 Hz
const FULL_USER_INTERVAL_MS = 100; // fullscreen: user updates at most 10 Hz
const FALLBACK_FRAME_MS = 50; // in case window rAF is throttled (e.g. during an XR session)
const TAP_SLOP = 10;
const TAP_MS = 350;
const MINI_TAP_MS = 800;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_DIST = 40;
const CULL_MARGIN = 16;
const FINISH_CELL = 4;
const BG = '#f3f1ec';
const INNER_FILL = 'rgba(255, 42, 42, 0.45)';
const OUTER_FILL = 'rgba(255, 42, 42, 0.22)';
// Extended-zone polygon: translucent orange, drawn only outside the inner/outer zones (under them).
const EXTENDED_FILL = 'rgba(255, 150, 0, 0.5)';
const COVERAGE_STROKE = 'rgba(70, 70, 70, 0.8)';
const PIER_STROKE = '#444444';
const SLIPWAY_FILL = OSM_TRANSPORT_ICON_COLOR;
const SLIPWAY_ICON = osmIcon('leisure=slipway');
const SLIPWAY_BORDER = '#ffffff';
const OSM_PIER_POINT_FILL = '#9e9e9e';
const SLIPWAY_LABEL_MIN_ZOOM = 16;
const USER_BLUE = [26, 115, 232] as const;
const USER_GREY = [128, 132, 140] as const;
const DEFAULT_CENTER: LatLon = { lat: 47.0535, lon: 8.3155 }; // Luzerner Seebecken
// Short form for the 150 px minimap, where the full attribution can't fit on one line.
const MINI_ATTRIBUTION = '© OSM, VersaTiles';

/**
 * Create the 2D map. The element sizes itself per mode:
 *  - 'mini': var(--uzm-mini-size, 150px) square, rounded; position it via its parent (HUD slot).
 *  - 'full': position: fixed; inset: 0 (fills the viewport, or the nearest transformed ancestor,
 *    i.e. the counter-rotated HUD root). Override `.uzm--full` if a different container is wanted.
 *  - 'hidden': display: none, no rendering, canvas memory released.
 */
export function createMapView(opts: MapViewOptions = {}): MapViewExt {
  ensureMapStyles();
  const source = opts.tileSource ?? createShortbreadSource();
  const miniZoom = opts.miniZoom ?? 15;

  // ─── DOM ──────────────────────────────────────────────────────────────────
  const root = document.createElement('div');
  root.className = 'uzm uzm--hidden';
  root.setAttribute('data-uzm', '');
  const canvas = document.createElement('canvas');
  canvas.className = 'uzm-canvas';
  const attr = document.createElement('div');
  attr.className = 'uzm-attr';
  attr.textContent = source.attribution;
  function updateAttribution(): void {
    attr.textContent = mode === 'mini' ? MINI_ATTRIBUTION : source.attribution;
  }
  const controls = document.createElement('div');
  controls.className = 'uzm-controls';
  const btnClose = button('uzm-close', '×', 'Karte schliessen');
  const btnIn = button('uzm-zin', '+', 'Hineinzoomen');
  const btnOut = button('uzm-zout', '−', 'Herauszoomen');
  const btnCenter = button('uzm-center', '', 'Auf Position zentrieren');
  btnCenter.innerHTML =
    '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><circle cx="12" cy="12" r="4" fill="currentColor"/>' +
    '<circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="2"/>' +
    '<path d="M12 1v4M12 19v4M1 12h4M19 12h4" stroke="currentColor" stroke-width="2"/></svg><span>Zentrieren</span>';
  controls.append(btnClose, btnIn, btnOut, btnCenter);
  root.append(canvas, attr, controls);
  const ctx = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;

  // ─── State ────────────────────────────────────────────────────────────────
  let mode: MapMode = 'hidden';
  let destroyed = false;
  let rotation = 0;
  let dpr = 1;
  const view: View = { cx: lonToX(DEFAULT_CENTER.lon), cy: latToY(DEFAULT_CENTER.lat), zoom: 12, width: 0, height: 0 };
  let savedFull: { cx: number; cy: number; zoom: number } | null = null;
  let fullZoom = opts.fullZoom ?? 15;
  let follow = true;
  let user: LatLon | null = null;
  let lastPos: LatLon | null = null;
  let headingDeg: number | null = null;
  let accuracyM: number | null = null;
  // Last-known cached position (localStorage), for display only until a live fix arrives.
  let cachedUser: LatLon | null = null;
  let cachedAccuracyM: number | null = null;
  let lakes: ProjectedLake[] = [];
  let anim: {
    t0: number;
    dur: number;
    z0: number;
    z1: number;
    anchored: boolean;
    ax: number;
    ay: number;
    wx: number;
    wy: number;
  } | null = null;
  // Snapshot of what was last drawn (to skip redundant redraws on user updates).
  const drawn = { has: false, active: false, x: 0, y: 0, heading: null as number | null, accPx: 0 };
  const stats = { draws: 0, lastDrawMs: 0, maxDrawMs: 0 };

  const requestFullCbs = new Set<() => void>();
  const closeCbs = new Set<() => void>();

  const tiles =
    opts.tiles === false ? null : new TileManager(source, () => invalidate(mode === 'mini' ? MINI_INTERVAL_MS : 0));

  // Reused scratch objects (no allocation in the per-draw hot loops).
  const tmp: Point = { x: 0, y: 0 };
  const xf: ScreenXform = { scale: 1, ox: 0, oy: 0 };
  const cull: CullRect = { xmin: 0, ymin: 0, xmax: 0, ymax: 0 };

  // ─── Scheduling ───────────────────────────────────────────────────────────
  let rafId = 0;
  let fallbackId = 0;
  let delayId = 0;
  let pendingAt = Infinity;
  let lastDrawAt = -Infinity;

  function invalidate(minIntervalMs: number): void {
    if (mode === 'hidden' || destroyed) return;
    const now = performance.now();
    const at = Math.max(now, lastDrawAt + minIntervalMs);
    if (at >= pendingAt) return; // an earlier draw is already scheduled
    cancelScheduled();
    pendingAt = at;
    if (at - now > 1) delayId = window.setTimeout(armFrame, at - now);
    else armFrame();
  }

  function armFrame(): void {
    delayId = 0;
    rafId = requestAnimationFrame(onFrame);
    fallbackId = window.setTimeout(onFrame, FALLBACK_FRAME_MS);
  }

  function onFrame(): void {
    cancelScheduled();
    pendingAt = Infinity;
    render();
  }

  function cancelScheduled(): void {
    if (rafId) cancelAnimationFrame(rafId);
    if (fallbackId) clearTimeout(fallbackId);
    if (delayId) clearTimeout(delayId);
    rafId = fallbackId = delayId = 0;
    pendingAt = Infinity;
  }

  // ─── Layout ───────────────────────────────────────────────────────────────
  function measure(): boolean {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const d = window.devicePixelRatio || 1;
    const bw = Math.round(w * d);
    const bh = Math.round(h * d);
    const changed = w !== view.width || h !== view.height || d !== dpr;
    view.width = w;
    view.height = h;
    dpr = d;
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw;
      canvas.height = bh;
    }
    return changed;
  }

  const ro = new ResizeObserver(() => {
    if (mode === 'hidden') return;
    if (measure()) invalidate(0);
  });
  ro.observe(root);

  // ─── Rendering ────────────────────────────────────────────────────────────
  function render(): void {
    if (mode === 'hidden' || destroyed) return;
    if (view.width === 0 || view.height === 0) measure();
    if (view.width === 0 || view.height === 0) return;
    const t0 = performance.now();
    lastDrawAt = t0;

    if (anim) stepAnimation(t0);
    const pos = user ?? lastPos ?? cachedUser;
    if (follow && pos) {
      view.cx = lonToX(pos.lon);
      view.cy = latToY(pos.lat);
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (tiles) drawTiles(tiles);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const ws = worldSize(view.zoom);
    xf.scale = ws;
    xf.ox = view.width / 2 - view.cx * ws;
    xf.oy = view.height / 2 - view.cy * ws;
    cull.xmin = -CULL_MARGIN;
    cull.ymin = -CULL_MARGIN;
    cull.xmax = view.width + CULL_MARGIN;
    cull.ymax = view.height + CULL_MARGIN;
    drawZones();
    drawSlipways();
    drawOsmPierPoints();
    drawUser(pos);
    if (mode === 'full') drawScaleBar();

    btnCenter.classList.toggle('uzm-center--active', follow && pos !== null);
    const dt = performance.now() - t0;
    stats.draws++;
    stats.lastDrawMs = dt;
    if (dt > stats.maxDrawMs) stats.maxDrawMs = dt;
    if (anim) invalidate(0);
  }

  function drawTiles(tm: TileManager): void {
    const tz = tileZoomFor(view.zoom, source.maxNativeZoom);
    const n = 1 << tz;
    const ws = worldSize(view.zoom);
    const r = tileRange(view, tz);
    const ccx = view.cx * n;
    const ccy = view.cy * n;
    tm.begin();
    for (let ty = r.y0; ty <= r.y1; ty++) {
      for (let tx = r.x0; tx <= r.x1; tx++) {
        const x = ((tx % n) + n) % n;
        // Device-pixel snapped destination rect (no seams between neighbours).
        const left = Math.round(((tx / n - view.cx) * ws + view.width / 2) * dpr);
        const right = Math.round((((tx + 1) / n - view.cx) * ws + view.width / 2) * dpr);
        const top = Math.round(((ty / n - view.cy) * ws + view.height / 2) * dpr);
        const bottom = Math.round((((ty + 1) / n - view.cy) * ws + view.height / 2) * dpr);
        const img = tm.image(tz, x, ty);
        if (img) {
          ctx.drawImage(img, left, top, right - left, bottom - top);
          continue;
        }
        const dx = tx + 0.5 - ccx;
        const dy = ty + 0.5 - ccy;
        tm.want(tz, x, ty, dx * dx + dy * dy);
        drawTileFallback(tm, tz, x, ty, left, top, right - left, bottom - top);
      }
    }
    tm.flush();
  }

  /** While a tile loads: draw the matching part of a cached ancestor, else cached children. */
  function drawTileFallback(tm: TileManager, tz: number, x: number, y: number, l: number, t: number, w: number, h: number): void {
    for (let d = 1; d <= 6 && tz - d >= 0; d++) {
      const img = tm.image(tz - d, x >> d, y >> d);
      if (img) {
        const sub = img.width / (1 << d);
        ctx.drawImage(img, (x - ((x >> d) << d)) * sub, (y - ((y >> d) << d)) * sub, sub, sub, l, t, w, h);
        return;
      }
    }
    if (tz + 1 > source.maxNativeZoom) return;
    for (let i = 0; i < 4; i++) {
      const cx = 2 * x + (i & 1);
      const cy = 2 * y + (i >> 1);
      const img = tm.image(tz + 1, cx, cy);
      if (img) {
        const hw = w / 2;
        const hh = h / 2;
        ctx.drawImage(img, l + (i & 1) * hw, t + (i >> 1) * hh, hw, hh);
      }
    }
  }

  function drawZones(): void {
    const outerP = new Path2D();
    const innerP = new Path2D();
    const extendedP = new Path2D();
    const covP = new Path2D();
    const extBand = new Path2D();
    const extSquares = new Path2D();
    const pierP = new Path2D();
    let nOuter = 0;
    let nInner = 0;
    let nExtended = 0;
    let nCov = 0;
    let nExt = 0;
    let nPier = 0;
    for (const lake of lakes) {
      if (!bboxVisible(lake.x0, lake.y0, lake.x1, lake.y1, xf, cull)) continue;
      for (const r of lake.outer) nOuter += appendCulled(outerP, r, true, xf, cull, 0.5);
      for (const r of lake.inner) nInner += appendCulled(innerP, r, true, xf, cull, 0.5);
      for (const r of lake.extended) nExtended += appendCulled(extendedP, r, true, xf, cull, 0.5);
      for (const r of lake.coverage) nCov += appendCulled(covP, r, true, xf, cull, 0.75);
      for (const l of lake.extendedLines) {
        const k = appendCulled(extBand, l, false, xf, cull, 0.5);
        if (k > 0) nExt += appendFinishSquares(extSquares, l, xf, cull, FINISH_CELL);
      }
      for (const l of lake.pierLines) nPier += appendCulled(pierP, l, false, xf, cull, 0.5);
      for (const l of lake.osmPierLines) nPier += appendCulled(pierP, l, false, xf, cull, 0.5);
    }
    // The extended zone lies under the inner/outer zones: orange fill only where no
    // red zone is, so the red zones keep their pure colour. Inner and outer are disjoint bands,
    // so viewport rect + both zone paths with 'evenodd' is the area outside them.
    if (nExtended) {
      const outside = new Path2D();
      outside.rect(cull.xmin, cull.ymin, cull.xmax - cull.xmin, cull.ymax - cull.ymin);
      outside.addPath(outerP);
      outside.addPath(innerP);
      ctx.save();
      ctx.clip(outside, 'evenodd');
      ctx.fillStyle = EXTENDED_FILL;
      ctx.fill(extendedP, 'evenodd');
      ctx.restore();
    }
    if (nOuter) {
      ctx.fillStyle = OUTER_FILL;
      ctx.fill(outerP, 'evenodd');
    }
    if (nInner) {
      ctx.fillStyle = INNER_FILL;
      ctx.fill(innerP, 'evenodd');
    }
    if (nCov) {
      ctx.strokeStyle = COVERAGE_STROKE;
      ctx.lineWidth = 1.2;
      ctx.setLineDash([5, 4]);
      ctx.stroke(covP);
      ctx.setLineDash([]);
    }
    if (nExt) {
      ctx.lineCap = 'butt';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(20, 20, 20, 0.9)';
      ctx.lineWidth = 2 * FINISH_CELL + 2;
      ctx.stroke(extBand);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 * FINISH_CELL;
      ctx.stroke(extBand);
      ctx.fillStyle = '#141414';
      ctx.fill(extSquares);
    }
    if (nPier) {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = PIER_STROKE;
      ctx.lineWidth = 1.5;
      ctx.stroke(pierP);
    }
  }

  function drawSlipways(): void {
    const full = mode === 'full';
    // Blue disc (~26 px full map / ~18 px minimap, CSS px) with the official OSM Carto
    // leisure=slipway symbol in white on top.
    const r = full ? 13 : 9;
    const showLabel = full && view.zoom >= SLIPWAY_LABEL_MIN_ZOOM;
    for (const lake of lakes) {
      if (!bboxVisible(lake.x0, lake.y0, lake.x1, lake.y1, xf, cull)) continue;
      for (const s of lake.slipways) {
        const x = s.x * xf.scale + xf.ox;
        const y = s.y * xf.scale + xf.oy;
        if (x < cull.xmin - r || x > cull.xmax + r || y < cull.ymin - r || y > cull.ymax + r) continue;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, 2 * Math.PI);
        ctx.fillStyle = SLIPWAY_FILL;
        ctx.fill();
        ctx.lineWidth = full ? 2 : 1.5;
        ctx.strokeStyle = SLIPWAY_BORDER;
        ctx.stroke();
        if (SLIPWAY_ICON) {
          ctx.fillStyle = SLIPWAY_BORDER;
          drawOsmIcon(ctx, SLIPWAY_ICON, x, y, r * 1.35);
        }
        if (showLabel && s.name) {
          ctx.font = '600 11px system-ui, sans-serif';
          ctx.textBaseline = 'middle';
          ctx.lineWidth = 3;
          ctx.strokeStyle = 'rgba(255,255,255,0.9)';
          ctx.strokeText(s.name, x + r + 4, y);
          ctx.fillStyle = '#222';
          ctx.fillText(s.name, x + r + 4, y);
        }
      }
    }
  }

  function drawOsmPierPoints(): void {
    const r = 3;
    for (const lake of lakes) {
      if (!bboxVisible(lake.x0, lake.y0, lake.x1, lake.y1, xf, cull)) continue;
      for (const p of lake.osmPierPoints) {
        const x = p.x * xf.scale + xf.ox;
        const y = p.y * xf.scale + xf.oy;
        if (x < cull.xmin - r || x > cull.xmax + r || y < cull.ymin - r || y > cull.ymax + r) continue;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, 2 * Math.PI);
        ctx.fillStyle = OSM_PIER_POINT_FILL;
        ctx.fill();
      }
    }
  }

  function drawUser(pos: LatLon | null): void {
    const active = user !== null;
    // Cache-only: no live fix and no stale-within-session position either, just the localStorage
    // last-known fix ("alte Position") — drawn hollow/grey, distinct from the solid grey used for a
    // stale-but-live-session position (lastPos).
    const cacheOnly = !active && lastPos === null && cachedUser !== null;
    drawn.has = pos !== null;
    drawn.active = active;
    drawn.heading = headingDeg;
    drawn.accPx = 0;
    if (!pos) return;
    const wx = lonToX(pos.lon);
    const wy = latToY(pos.lat);
    drawn.x = wx;
    drawn.y = wy;
    worldToScreen(view, wx, wy, tmp);
    const x = tmp.x;
    const y = tmp.y;
    if (x < -200 || y < -200 || x > view.width + 200 || y > view.height + 200) return;
    const [r, g, b] = active ? USER_BLUE : USER_GREY;
    const rgba = (a: number) => `rgba(${r}, ${g}, ${b}, ${a})`;
    const effAccuracyM = active ? accuracyM : cacheOnly ? cachedAccuracyM : null;

    if (effAccuracyM !== null && effAccuracyM > 0) {
      const accPx = Math.min(effAccuracyM / metersPerPixel(pos.lat, view.zoom), 5000);
      drawn.accPx = accPx;
      if (accPx > 9) {
        ctx.beginPath();
        ctx.arc(x, y, accPx, 0, 2 * Math.PI);
        ctx.fillStyle = rgba(0.13);
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = rgba(0.45);
        ctx.stroke();
      }
    }
    if (headingDeg !== null) {
      const len = mode === 'mini' ? 36 : 52;
      const a = ((headingDeg - 90) * Math.PI) / 180;
      const spread = (28 * Math.PI) / 180;
      const grad = ctx.createRadialGradient(x, y, 4, x, y, len);
      grad.addColorStop(0, rgba(0.85));
      grad.addColorStop(1, rgba(0.25));
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.arc(x, y, len, a - spread, a + spread);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
      // Thin outline so the cone stays readable over the red zone fills.
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(x, y, 8.5, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, 7.5, 0, 2 * Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, 5.5, 0, 2 * Math.PI);
    if (cacheOnly) {
      // Hollow ("alte Position"): outline only, no filled centre.
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = rgba(1);
      ctx.stroke();
    } else {
      ctx.fillStyle = rgba(1);
      ctx.fill();
    }
  }

  function drawScaleBar(): void {
    const mpp = metersPerPixel(yToLat(view.cy), view.zoom);
    const s = niceScale(mpp, 110);
    const x0 = 14;
    const y0 = view.height - 40;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
    for (const [style, width] of [
      ['rgba(255,255,255,0.9)', 5],
      ['#222', 2],
    ] as const) {
      ctx.strokeStyle = style;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(x0, y0 - 6);
      ctx.lineTo(x0, y0);
      ctx.lineTo(x0 + s.px, y0);
      ctx.lineTo(x0 + s.px, y0 - 6);
      ctx.stroke();
    }
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.textBaseline = 'bottom';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.strokeText(s.label, x0 + 4, y0 - 3);
    ctx.fillStyle = '#222';
    ctx.fillText(s.label, x0 + 4, y0 - 3);
  }

  // ─── View helpers ─────────────────────────────────────────────────────────
  function clampZoom(z: number): number {
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
  }

  /**
   * No live/stale/cached position at all: centre on the default preset lake area (Vierwaldstättersee,
   * Luzern) at a normal readable zoom, instead of a zoomed-out "fit all lake data" overview (which,
   * with only sparse scattered markers and no basemap yet loaded, can look like an empty box) or
   * leaving a stale/uninitialised view.
   */
  function centerOnDefault(zoom: number): void {
    view.cx = lonToX(DEFAULT_CENTER.lon);
    view.cy = latToY(DEFAULT_CENTER.lat);
    view.zoom = zoom;
  }

  function animateZoom(z1: number, ax: number, ay: number, anchored: boolean): void {
    screenToWorld(view, ax, ay, tmp);
    anim = { t0: performance.now(), dur: 260, z0: view.zoom, z1: clampZoom(z1), anchored, ax, ay, wx: tmp.x, wy: tmp.y };
    invalidate(0);
  }

  function stepAnimation(now: number): void {
    if (!anim) return;
    const t = Math.min(1, (now - anim.t0) / anim.dur);
    const e = 1 - (1 - t) * (1 - t) * (1 - t);
    const z = anim.z0 + (anim.z1 - anim.z0) * e;
    if (anim.anchored) placeWorldAt(view, anim.wx, anim.wy, anim.ax, anim.ay, z);
    else view.zoom = z;
    if (t >= 1) anim = null;
  }

  function userChangedVisibly(): boolean {
    const pos = user ?? lastPos;
    if ((pos !== null) !== drawn.has || (user !== null) !== drawn.active) return true;
    if (!pos) return false;
    const ws = worldSize(view.zoom);
    if (Math.abs((lonToX(pos.lon) - drawn.x) * ws) > 0.5 || Math.abs((latToY(pos.lat) - drawn.y) * ws) > 0.5) return true;
    if ((headingDeg === null) !== (drawn.heading === null)) return true;
    if (headingDeg !== null && drawn.heading !== null) {
      const d = Math.abs(normDeg(headingDeg - drawn.heading + 180) - 180);
      if (d > 1.5) return true;
    }
    if (user && accuracyM !== null) {
      const accPx = accuracyM / metersPerPixel(pos.lat, view.zoom);
      if (Math.abs(accPx - drawn.accPx) > 1 && (accPx > 9 || drawn.accPx > 9)) return true;
    }
    return false;
  }

  // ─── Input ────────────────────────────────────────────────────────────────
  interface Ptr {
    /** Last position applied to a pan (held at the down position until the tap slop is exceeded). */
    x: number;
    y: number;
    /** Latest position (used for pinch). */
    lx: number;
    ly: number;
  }
  const ptrs = new Map<number, Ptr>();
  let rect: DOMRect | null = null;
  const gesture = { t: 0, sx: 0, sy: 0, moved: false, multi: false };
  const pinch = { d0: 1, z0: 0, wx: 0, wy: 0 };
  const lastTap = { t: -1e9, x: 0, y: 0 };

  function toLocal(clientX: number, clientY: number, out: Point): Point {
    if (!rect) rect = canvas.getBoundingClientRect();
    return clientToLocal(clientX, clientY, rect, canvas.clientWidth, canvas.clientHeight, rotation, out);
  }

  function twoPointers(): [Ptr, Ptr] {
    const it = ptrs.values();
    return [it.next().value as Ptr, it.next().value as Ptr];
  }

  function startPinch(): void {
    const [a, b] = twoPointers();
    pinch.d0 = Math.max(Math.hypot(b.lx - a.lx, b.ly - a.ly), 1);
    pinch.z0 = view.zoom;
    screenToWorld(view, (a.lx + b.lx) / 2, (a.ly + b.ly) / 2, tmp);
    pinch.wx = tmp.x;
    pinch.wy = tmp.y;
  }

  function onPointerDown(e: PointerEvent): void {
    if (mode === 'hidden') return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    rect = canvas.getBoundingClientRect();
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* ignore (synthetic pointers) */
    }
    const p = toLocal(e.clientX, e.clientY, tmp);
    ptrs.set(e.pointerId, { x: p.x, y: p.y, lx: p.x, ly: p.y });
    if (ptrs.size === 1) {
      gesture.t = e.timeStamp;
      gesture.sx = e.clientX;
      gesture.sy = e.clientY;
      gesture.moved = false;
      gesture.multi = false;
    } else {
      gesture.multi = true;
      gesture.moved = true;
      if (mode === 'full' && ptrs.size === 2) {
        follow = false;
        startPinch();
      }
    }
    anim = null;
  }

  function onPointerMove(e: PointerEvent): void {
    const ptr = ptrs.get(e.pointerId);
    if (!ptr) return;
    const p = toLocal(e.clientX, e.clientY, tmp);
    ptr.lx = p.x;
    ptr.ly = p.y;
    if (!gesture.moved && Math.hypot(e.clientX - gesture.sx, e.clientY - gesture.sy) > TAP_SLOP) gesture.moved = true;
    if (mode !== 'full') return;
    if (ptrs.size === 1) {
      // Below the tap slop the pointer keeps its down position, so the first real pan step
      // includes the whole offset (content stays exactly under the finger).
      if (!gesture.moved) return;
      const dx = p.x - ptr.x;
      const dy = p.y - ptr.y;
      ptr.x = p.x;
      ptr.y = p.y;
      if (dx === 0 && dy === 0) return;
      follow = false;
      panBy(view, dx, dy);
      invalidate(0);
    } else {
      ptr.x = p.x;
      ptr.y = p.y;
      const [a, b] = twoPointers();
      const d = Math.max(Math.hypot(b.lx - a.lx, b.ly - a.ly), 1);
      const z = clampZoom(pinch.z0 + Math.log2(d / pinch.d0));
      placeWorldAt(view, pinch.wx, pinch.wy, (a.lx + b.lx) / 2, (a.ly + b.ly) / 2, z);
      invalidate(0);
    }
  }

  function onPointerUp(e: PointerEvent): void {
    const ptr = ptrs.get(e.pointerId);
    if (!ptr) return;
    ptrs.delete(e.pointerId);
    if (ptrs.size >= 2 && mode === 'full') startPinch();
    if (ptrs.size > 0 || gesture.multi || gesture.moved || e.type !== 'pointerup') return;
    if (e.timeStamp - gesture.t > (mode === 'mini' ? MINI_TAP_MS : TAP_MS)) return;
    onTap(ptr.lx, ptr.ly, e.timeStamp);
  }

  function onTap(x: number, y: number, t: number): void {
    if (mode === 'mini') {
      emit(requestFullCbs);
      return;
    }
    if (mode !== 'full') return;
    if (t - lastTap.t < DOUBLE_TAP_MS && Math.hypot(x - lastTap.x, y - lastTap.y) < DOUBLE_TAP_DIST) {
      lastTap.t = -1e9;
      follow = false;
      animateZoom(Math.round(view.zoom) + 1, x, y, true);
    } else {
      lastTap.t = t;
      lastTap.x = x;
      lastTap.y = y;
    }
  }

  function onWheel(e: WheelEvent): void {
    if (mode !== 'full') return;
    e.preventDefault();
    rect = canvas.getBoundingClientRect();
    const p = toLocal(e.clientX, e.clientY, tmp);
    const k = e.deltaMode === 1 ? 0.05 : e.deltaMode === 2 ? 0.5 : 0.002;
    anim = null;
    follow = false;
    zoomAround(view, p.x, p.y, clampZoom(view.zoom - e.deltaY * k));
    invalidate(0);
  }

  function onKey(e: KeyboardEvent): void {
    if (mode === 'mini' && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      emit(requestFullCbs);
      return;
    }
    if (mode !== 'full' || e.target !== root) return;
    const step = 80;
    let handled = true;
    if (e.key === 'Escape') emit(closeCbs);
    else if (e.key === '+' || e.key === '=') animateZoom(view.zoom + 1, view.width / 2, view.height / 2, false);
    else if (e.key === '-') animateZoom(view.zoom - 1, view.width / 2, view.height / 2, false);
    else if (e.key === 'ArrowLeft') panBy(view, step, 0);
    else if (e.key === 'ArrowRight') panBy(view, -step, 0);
    else if (e.key === 'ArrowUp') panBy(view, 0, step);
    else if (e.key === 'ArrowDown') panBy(view, 0, -step);
    else handled = false;
    if (handled) {
      if (e.key.startsWith('Arrow')) follow = false;
      e.preventDefault();
      invalidate(0);
    }
  }

  const stopXrSelect = (e: Event) => e.preventDefault();
  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  canvas.addEventListener('lostpointercapture', onPointerUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('contextmenu', stopXrSelect);
  root.addEventListener('keydown', onKey);
  // Taps on the map must not also trigger an XR 'select' in the dom-overlay.
  root.addEventListener('beforexrselect', stopXrSelect);

  btnClose.addEventListener('click', () => emit(closeCbs));
  btnIn.addEventListener('click', () => animateZoom(view.zoom + 1, view.width / 2, view.height / 2, false));
  btnOut.addEventListener('click', () => animateZoom(view.zoom - 1, view.width / 2, view.height / 2, false));
  btnCenter.addEventListener('click', () => {
    follow = true;
    if (view.zoom < 12) animateZoom(fullZoom, view.width / 2, view.height / 2, false);
    invalidate(0);
  });

  function emit(cbs: Set<() => void>): void {
    for (const cb of [...cbs]) {
      try {
        cb();
      } catch (err) {
        console.error('[map] callback failed', err);
      }
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────
  const api: MapViewExt = {
    element: root,

    setLakes(files: LakeFile[]): void {
      lakes = files.map(projectLake);
      // No live/stale/cached position yet: (re-)centre on the default preset area, unless the
      // view was set/moved explicitly. Once any position is known, that already drives the centre.
      if (!lastPos && !cachedUser && follow && mode !== 'hidden' && (mode === 'mini' || !savedFull)) {
        centerOnDefault(mode === 'mini' ? miniZoom : fullZoom);
      }
      invalidate(0);
    },

    setUser(p: LatLon | null, heading: number | null, accuracy: number | null): void {
      const hadPos = lastPos !== null || cachedUser !== null;
      user = p ? { lat: p.lat, lon: p.lon } : null;
      if (user) lastPos = user;
      headingDeg = heading === null || !Number.isFinite(heading) ? null : normDeg(heading);
      accuracyM = accuracy;
      if (mode === 'hidden') return;
      // First live fix: jump/re-centre from the data overview or cached position to the working zoom.
      if (!hadPos && user && follow) view.zoom = mode === 'mini' ? miniZoom : fullZoom;
      if (!userChangedVisibly()) return;
      invalidate(mode === 'mini' ? MINI_INTERVAL_MS : FULL_USER_INTERVAL_MS);
    },

    setCachedUser(p: LatLon | null, accuracy: number | null): void {
      cachedUser = p ? { lat: p.lat, lon: p.lon } : null;
      cachedAccuracyM = accuracy;
      if (mode === 'hidden') return;
      // Only affects the view while there is no live/stale position yet in this session.
      if (user !== null || lastPos !== null) return;
      if (cachedUser && follow) view.zoom = mode === 'mini' ? miniZoom : fullZoom;
      invalidate(0);
    },

    setMode(m: 'mini' | 'full' | 'hidden'): void {
      if (m === mode || destroyed) return;
      if (mode === 'full') {
        savedFull = { cx: view.cx, cy: view.cy, zoom: view.zoom };
        fullZoom = view.zoom;
      }
      mode = m;
      updateAttribution();
      root.className = `uzm uzm--${m}`;
      root.tabIndex = m === 'hidden' ? -1 : 0;
      if (m === 'mini') {
        root.setAttribute('role', 'button');
        root.setAttribute('aria-label', 'Karte öffnen');
      } else {
        root.setAttribute('role', 'application');
        root.setAttribute('aria-label', 'Karte');
      }
      ptrs.clear();
      anim = null;
      rect = null;
      if (m === 'hidden') {
        cancelScheduled();
        // Release the backing store; re-allocated on show.
        canvas.width = 0;
        canvas.height = 0;
        view.width = view.height = 0;
        return;
      }
      follow = true;
      measure();
      const pos = user ?? lastPos ?? cachedUser;
      if (m === 'mini') {
        view.zoom = pos ? miniZoom : view.zoom;
        if (!pos) centerOnDefault(miniZoom);
      } else {
        view.zoom = savedFull?.zoom ?? fullZoom;
        if (!pos) {
          if (savedFull) {
            view.cx = savedFull.cx;
            view.cy = savedFull.cy;
          } else centerOnDefault(fullZoom);
        }
      }
      lastDrawAt = -Infinity;
      invalidate(0);
    },

    setInputRotation(deg: number): void {
      rotation = normDeg(Math.round(deg));
      rect = null;
    },

    onRequestFull(cb: () => void): () => void {
      requestFullCbs.add(cb);
      return () => requestFullCbs.delete(cb);
    },

    onClose(cb: () => void): () => void {
      closeCbs.add(cb);
      return () => closeCbs.delete(cb);
    },

    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      cancelScheduled();
      ro.disconnect();
      tiles?.destroy();
      requestFullCbs.clear();
      closeCbs.clear();
      root.removeEventListener('beforexrselect', stopXrSelect);
      root.remove();
      canvas.width = canvas.height = 0;
    },

    getView(): MapViewState {
      return {
        lat: yToLat(view.cy),
        lon: xToLon(view.cx),
        zoom: view.zoom,
        follow,
        mode,
        width: view.width,
        height: view.height,
        rotation,
      };
    },

    setView(center: LatLon, zoom: number): void {
      follow = false;
      anim = null;
      view.cx = lonToX(center.lon);
      view.cy = latToY(center.lat);
      view.zoom = clampZoom(zoom);
      invalidate(0);
    },

    localToLatLon(x: number, y: number): LatLon {
      screenToWorld(view, x, y, tmp);
      return { lat: yToLat(tmp.y), lon: xToLon(tmp.x) };
    },

    latLonToLocal(p: LatLon): Point {
      const out = { x: 0, y: 0 };
      return worldToScreen(view, lonToX(p.lon), latToY(p.lat), out);
    },

    stats(): MapViewStats {
      const t: TileStats = tiles
        ? tiles.stats()
        : { cached: 0, loaded: 0, inflight: 0, requested: 0, failed: 0, maxRequestedZoom: -1 };
      return { ...t, ...stats };
    },
  };
  return api;
}

function button(cls: string, text: string, label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `uzm-btn ${cls}`;
  b.textContent = text;
  b.setAttribute('aria-label', label);
  b.title = label;
  return b;
}
